import React, { createContext, useContext, useEffect, useMemo } from 'react';

import { levelFocusKey } from '@/hooks/useLevelFocus';
import {
  useMathSubjectFocus,
  type UseMathSubjectFocusResult,
} from '@/hooks/useMathSubjectFocus';

import { useFocusTransition } from './focusTransitionContext';
import { useLevelFocusContext } from './levelFocusContext';

const MathSubjectFocusContext = createContext<UseMathSubjectFocusResult | null>(
  null
);

interface MathSubjectFocusProviderProps {
  children: React.ReactNode;
}

/**
 * Provides the student's durable E-Math / A-Math focus to a page subtree.
 *
 * Mounted per-page rather than once in a global shell: `/practice` (which
 * covers `/tasks` and the topic/session children through
 * `AdaptivePracticePage`), `/progress` and `/history` are the only surfaces
 * that read the focus, and hoisting it above every route would fire the
 * request on ~80 pages that will never use it. Per-page mounting is safe
 * because the underlying hook is react-query backed — the instances share one
 * cache entry, so they never disagree and a walk between them does not
 * refetch.
 */
export const MathSubjectFocusProvider: React.FC<
  MathSubjectFocusProviderProps
> = ({ children }) => {
  const levelFocus = useLevelFocusContext();
  const transition = useFocusTransition();
  const effectiveLevel = levelFocus.value ?? levelFocus.ownLevelStream;
  const value = useMathSubjectFocus({
    levelFocusKey: effectiveLevel ? levelFocusKey(effectiveLevel) : null,
    // When a LevelFocusProvider is present its first read decides the
    // applicability record. A surface with no level provider (History) gets
    // the inert `isLoading: false` fallback and retains its existing subject
    // behavior.
    enabled: !levelFocus.isLoading && !transition.isSaving,
  });
  const {
    enabled,
    isLoading,
    isFetching,
    isReady,
    error,
    refetch,
    isUpdating,
    value: focusValue,
    setValue,
  } = value;

  // A save can succeed while a newly mounted destination still needs this
  // applicability record (most visibly, History's level-less cache entry).
  // Treat a failed prerequisite as destination-content failure when it is
  // mandatory for a level change, absent, or still disagrees with an accepted
  // subject. Those destinations intentionally hold their content subtree, so
  // no page query exists yet to close the transition itself.
  useEffect(() => {
    const acceptedSubject = transition.target.mathSubject;
    const blocksSubjectTransition =
      transition.kind === 'subject' &&
      (!isReady ||
        acceptedSubject === undefined ||
        focusValue !== acceptedSubject);
    if (
      !transition.isContentLoading ||
      isFetching ||
      !error ||
      (transition.kind !== 'level' && !blocksSubjectTransition)
    ) {
      return;
    }
    transition.failContent(transition.id, error, () => {
      void refetch();
    });
  }, [transition, error, isFetching, isReady, focusValue, refetch]);
  // Memoize so consumers that subscribe to a single field don't re-render on
  // every parent render — `setValue` is already a stable ref.
  const memoized = useMemo(
    () => ({
      enabled,
      isLoading,
      isFetching,
      isReady,
      error,
      refetch,
      isUpdating,
      value: focusValue,
      setValue,
    }),
    [
      enabled,
      isLoading,
      isFetching,
      isReady,
      error,
      refetch,
      isUpdating,
      focusValue,
      setValue,
    ]
  );
  return (
    <MathSubjectFocusContext.Provider value={memoized}>
      {children}
    </MathSubjectFocusContext.Provider>
  );
};

/**
 * Access the current subject focus. With no provider mounted (a test
 * rendering the control in isolation, or a surface that deliberately doesn't
 * carry the setting) this returns a disabled fallback: the control renders
 * nothing and the focus resolves to `null`, so the page sends no paper and the
 * server applies the student's own.
 */
export const useMathSubjectFocusContext = (): UseMathSubjectFocusResult => {
  const ctx = useContext(MathSubjectFocusContext);
  if (ctx) return ctx;
  return {
    enabled: false,
    isLoading: false,
    isFetching: false,
    isReady: true,
    error: null,
    refetch: () => undefined,
    isUpdating: false,
    value: null,
    setValue: () => undefined,
  };
};
